import { syncFolders } from '../../lib/emailSync';
import EmailMessageActions from './EmailMessageActions';
import ResponsiveThreadActions from './ResponsiveThreadActions';
import EmailThreadBlock from './EmailThreadBlock';
import { groupEmailTimeline, visibleEmailThreadSession } from '../../lib/emailThreads';
import GroupParticipantsControl from './GroupParticipantsControl';
// BL-241 Gate 2 — right pane of the Communications redesign.
//
// Replaces the old raw-JSON detail card: conversation_log and raw_message_log are merged into
// one chronological list of chat bubbles (inbound left, outbound right), split wherever
// two consecutive entries sit more than 30 minutes apart. The reply composer is unchanged — same
// component, same send behaviour, only repositioned at the bottom of the pane.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import IntakeCaptureChecklist from '../shared/IntakeCaptureChecklist';
import ContactDetailsForm from './ContactDetailsForm';
import AppChatMessageMeta from './AppChatMessageMeta';
import { chatRpc, watchChat } from '../../lib/m9AppChat';
import StaffReplyComposer from './StaffReplyComposer';
import CommunicationsThreadSidePanel from './CommunicationsThreadSidePanel';
import ConversationOverlayActions from './ConversationOverlayActions';
import ConversationPresenceBand from './ConversationPresenceBand';
import useThreadScroll from './useThreadScroll';
import usePullToRefresh from '../../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../ui/PullToRefreshIndicator';
import { mergeGateEvents } from '../../lib/conversationGate';
import { CommunicationsStatusIcon } from './CommunicationsThreadFlagControl';
import ActionPillMenu from '../ui/ActionPillMenu';
import { normalizeOutboundPhoneAddress } from '../../lib/communicationsCompose';
import { emptyEntry, entriesOf, primaryEntry } from '../../lib/contactMethods';
import { personalAccountNameOf } from '../../lib/companyContactAdd';
import { captureChecklistStats, checklistTierColor } from '../../lib/communicationsChecklist';
import {
    FLAG_MENU_OPTIONS,
    isConversationClosed,
    isTerminalFlagStatus,
    normalizeSessionFlagStatus,
    QUIET_GREY,
    QUIET_TEXT_CLASS,
} from '../../lib/communicationsFlags';
import { highlightCapturedChecklistText } from '../../lib/communicationsHighlights';
import {
    buildThreadItems,
    INBOUND_MESSAGE_MEDIA_BUCKET,
    resolveContactAddress,
    resolveContactName,
    splitMissingTemplateValues,
} from '../../lib/communicationsThread';
import { formatRelativeAge } from '../../lib/communicationsPresence';
import { countUnreadInternalNotes, normalizeInternalNotes } from '../../lib/communicationsNotes';
import { AI_PAUSE_UNTIL_CLOSED_REASON, isAiThreadOpen, describeConversationAiStatus } from '../../lib/aiAssistantGate';
import { sendStaffReply } from '../../lib/staffReply';
import CreateReservationButton from './CreateReservationButton';
import ClickToCallButton from './ClickToCallButton';
import { usePhoneCapability } from '../../lib/phoneChannelApi';
import { canCallConversation } from '../../lib/outboundCall';
import ThreadActionsMenu from './ThreadActionsMenu';
import ResetTestContactDialog from '../ai/ResetTestContactDialog';
import { canResetTestContact } from '../../lib/testContactReset';
import useCommsViewport from '../../hooks/useCommsViewport';
import { isReconciled } from '../../lib/optimisticMessages';
import {
    formatEasternTimestamp, messageDeliveryState, threadAvatarLabel, threadDisplayName, threadFlag, threadHeaderSubtitle,
} from '../../lib/communicationsDisplay';
import StatusChip from '../ui/StatusChip';
import MessageBubble from './MessageBubble';
import ScheduledEditDialog from './ScheduledEditDialog';
import { useIsCompanyAdmin, useScheduledMessages } from './useScheduledMessages';
import { useAiThreadNotes } from './useAiThreadNotes';
import { buildScheduledThreadItems } from '../../lib/scheduledThread';
import { canEditScheduled, canManageScheduled, recipientTimeZoneForPhone } from '../../lib/scheduledSend';
import { resolveContactTimeZone } from '../../lib/contactTimeZone';
import { cancelScheduledMessage, loadScheduledEvents, sendScheduledNow, updateScheduledMessage } from '../../lib/scheduledSendApi';
import { voicemailStateKey } from '../../lib/voicemailState';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// BL-177 Gate 16: a template value that was never recorded renders as a quiet grey dash,
// never as a raw "{{n}}" placeholder.
function renderMessageText(text, keyPrefix) {
    return splitMissingTemplateValues(text).map((part, index) => (
        part.missing ? (
            <span key={`${keyPrefix}-missing-${index}`} className="text-[#E8EEF6]" title="Value not recorded" data-testid="missing-template-value">
                {part.text}
            </span>
        ) : <React.Fragment key={`${keyPrefix}-text-${index}`}>{part.text}</React.Fragment>
    ));
}

function formatStamp(at) {
    if (!at) return '';
    const date = new Date(at);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

// Internal-notes list stamp: time only for today, short date + time otherwise.
function formatNoteStamp(at) {
    if (!at) return '';
    const date = new Date(at);
    if (Number.isNaN(date.getTime())) return '';
    const sameDay = date.toDateString() === new Date().toDateString();
    return sameDay
        ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function toTimestamp(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function capturedAtFor(entry = {}) {
    return toTimestamp(entry.captured_at || entry.capturedAt || entry.source_at || entry.sourceAt);
}

function hasChecklistAttribution(checklist = {}) {
    return Object.values(checklist || {}).some((entry) => entry?.captured && capturedAtFor(entry) !== null);
}

function buildConversationBlocks(items = []) {
    const blocks = [];
    let current = { id: 'conversation-block-1', divider: null, messages: [] };

    items.forEach((item) => {
        if (item.kind === 'divider') {
            if (current.messages.length > 0) blocks.push(current);
            current = { id: `conversation-block-${blocks.length + 1}`, divider: item, messages: [] };
            return;
        }
        current.messages.push(item);
    });

    if (current.messages.length > 0) blocks.push(current);
    return blocks.map((block, index) => {
        const stamps = block.messages.flatMap((message) => message.kind === 'email-thread'
            ? message.messages.map(email => email.at) : [message.at]).filter(Number.isFinite);
        return {
            ...block,
            id: `conversation-block-${index + 1}`,
            startAt: stamps.length ? Math.min(...stamps) : null,
            endAt: stamps.length ? Math.max(...stamps) : null,
        };
    });
}

function checklistForBlock(checklist = {}, block = {}) {
    const scoped = {};
    Object.entries(checklist || {}).forEach(([field, entry]) => {
        const capturedAt = capturedAtFor(entry);
        const capturedInBlock = Boolean(
            entry?.captured
            && capturedAt !== null
            && block.startAt !== null
            && block.endAt !== null
            && capturedAt >= block.startAt
            && capturedAt <= block.endAt
        );
        scoped[field] = {
            ...entry,
            captured: capturedInBlock,
            value: capturedInBlock ? entry?.value : null,
        };
    });
    return scoped;
}

function ConversationChecklistPill({
    checklist,
    blockId,
    isOpen,
    onToggle,
    theme = 'light',
}) {
    const dark = theme === 'dark';
    const stats = captureChecklistStats(checklist);
    const label = `Session Checklist ${stats.captured}/${stats.total} · ${stats.percent}%`;
    const color = checklistTierColor(stats.percent);
    const labelCls = dark ? 'text-gray-400' : 'text-gray-500';
    const dividerCls = dark ? 'border-gray-700' : 'border-gray-100';

    return (
        <div className="relative flex min-w-0 flex-wrap items-center justify-center gap-2">
            <button
                type="button"
                onClick={() => onToggle(blockId)}
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden rounded-full border bg-transparent px-2 py-1 text-xs"
                style={{
                    '--checklist-tier-color': color,
                    borderColor: 'var(--checklist-tier-color)',
                    color: 'var(--checklist-tier-color)',
                }}
                aria-expanded={isOpen}
                aria-label="Toggle capture checklist"
                title={label}
            >
                <span
                    aria-hidden="true"
                    className="h-3.5 w-3.5 shrink-0 rounded-full"
                    style={{ background: `conic-gradient(var(--checklist-tier-color) ${stats.percent}%, transparent 0)`, border: '1px solid currentColor' }}
                />
                <span className="min-w-0 truncate">{label}</span>
            </button>
            {isOpen && (
                <div className={`absolute right-0 top-9 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border p-3 shadow-lg ${dark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wide">Capture Checklist</h4>
                        <span className={`text-xs ${labelCls}`}>
                            {stats.missingRequired > 0 ? `${stats.missingRequired} required missing` : 'Complete'}
                        </span>
                    </div>
                    <IntakeCaptureChecklist checklist={checklist} theme={theme} />
                    <p className={`mt-2 text-center text-xs ${labelCls}`}>This checklist reflects this session block.</p>
                    <p className={`mt-2 pt-2 border-t text-center text-xs ${dividerCls} ${labelCls}`}>* = Required</p>
                </div>
            )}
        </div>
    );
}

function ConversationEndMarker({ theme = 'light' }) {
    const dark = theme === 'dark';
    return (
        <div className="flex justify-center py-0.5">
            <span
                className="rounded-full border px-1.5 py-0.5 text-[10px] leading-none"
                style={dark
                    ? { borderColor: 'rgb(251 146 60 / 0.6)', color: '#FDBA74' }
                    : { borderColor: '#FDBA74', color: '#EA580C' }}
            >
                Conversation End
            </span>
        </div>
    );
}

function InternalNotesStatusControl({ intakeSession, onSetFlag, theme = 'light' }) {
    const dark = theme === 'dark';
    const [saving, setSaving] = useState(false);
    // Gate 17c: while the conversation is closed the control shows Closed only, in grey.
    const closedQuiet = isConversationClosed(intakeSession);
    const status = closedQuiet ? 'closed' : normalizeSessionFlagStatus(intakeSession);
    const matchedOption = FLAG_MENU_OPTIONS.find((option) => option.status === status) || FLAG_MENU_OPTIONS[0];
    const selectedOption = closedQuiet ? { ...matchedOption, className: QUIET_TEXT_CLASS } : matchedOption;
    const disabled = saving || typeof onSetFlag !== 'function';
    const neutralPillCls = dark
        ? 'border-gray-600 bg-gray-900/40 text-gray-400'
        : 'border-gray-300 bg-white text-gray-500';

    const changeStatus = async (option) => {
        const nextStatus = option.status;
        if (!onSetFlag) return;
        setSaving(true);
        try {
            await onSetFlag({
                status: nextStatus,
                urgency: null,
                active: isTerminalFlagStatus(nextStatus) ? false : Boolean(intakeSession?.flag_active),
                note: '',
            });
        } finally {
            setSaving(false);
        }
    };

    // BL-177 Gate 17d: the status pill's menu is an Action Pill menu. Each status is its own pill:
    // its glyph at rest, the name sliding out on hover (always shown on touch).
    const statusOptions = FLAG_MENU_OPTIONS.map((option) => ({
        key: option.key,
        label: option.label,
        icon: option.glyph,
        iconClassName: option.className,
        title: option.label,
        active: option.status === status,
        onSelect: () => changeStatus(option),
    }));

    return (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            <div className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Thread status</div>
            <ActionPillMenu
                className="max-w-full"
                menuLabel="Set internal note status"
                options={statusOptions}
                // Opens upward: the control sits at the bottom of the pane.
                placement="top"
                align="end"
                size="compact"
                disabled={disabled}
                triggerLabel="Internal note status"
                triggerClassName={`inline-flex w-auto max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${selectedOption.status === 'none' ? neutralPillCls : `border-current bg-transparent ${selectedOption.className}`} ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
            >
                <CommunicationsStatusIcon session={intakeSession} bare />
                <span className="font-medium">{selectedOption.label}</span>
            </ActionPillMenu>
        </div>
    );
}

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

export function contactFormFromThread(intakeSession = {}, contact = null, account = null) {
    // BL-177 PR C: every phone and email on file shows as a labeled entry; the thread's own address is added when the person has none yet.
    const seeded = (kind, address) => {
        const rows = entriesOf(contact, kind).filter(row => (row.value || '').trim());
        if (rows.length) return rows;
        return [{ ...emptyEntry(kind), is_primary: true, value: address || '' }];
    };
    const phones = seeded('phone', intakeSession?.contact_phone);
    const emails = seeded('email', intakeSession?.contact_email);
    const phone = firstString(primaryEntry(phones)?.value);
    const email = firstString(primaryEntry(emails)?.value);
    const name = firstString(contact?.name, account?.account_name, intakeSession?.contact_email, intakeSession?.contact_phone);
    // BL-177 Step 2m: no consent or preference fields. Communication preferences belong to the client (the form shows them read-only).
    return {
        name,
        accountName: firstString(account?.account_name, contact?.name, name),
        accountKind: account ? (account.account_kind || (account.is_business === false ? 'personal' : 'business')) : 'business',
        individual: (account?.account_kind || (account?.is_business === false ? 'personal' : 'business')) === 'personal' && Boolean(account),
        email,
        phone,
        emails,
        phones,
    };
}

function IntakeSessionDetailCard({
    intakeSession,
    account = null,
    contact = null,
    myContact = null,   // BL-177 PR D6: the signed-in person's OWN My Contact for this conversation (names it only on their screen)
    staffNames = {},
    participantNames = {},
    // BL-177 Step 2l: Company inbox / My inbox state for this conversation (see CommunicationsView):
    // { mode: 'company' | 'mine' | 'colleague', people, peopleLoading, peopleError, busy, readOnlyBy, onTake, onLeave, onPassTo, onInvite, onJoin }
    access = null,
    onClose,
    // BL-177 Step 2n: on phones the conversation is its own screen; "← Back" returns to the conversation list.
    onBack = null,
    onSaveContact,
    // Comms next pass (5b): the thread list's "Add to CRM" opens this thread with the contact form already open
    // ({ sessionId, nonce } — a new nonce re-opens it). The form is prefilled from the thread's number / email.
    openContactEditorRequest = null,
    onWhatsAppFlagChange,
    onDeleteThread,
    onReplySent,
    onMarkInternalNotesRead,
    onAddInternalNote,
    onSetFlag,
    currentUserId = null,
    platformDoorContext = null,
    headerBrandEntityId = '',
    aiPauseSettings = null,
    onReservationChanged,
    onHandBackToAi,
    onToggleArchive,
    onTestContactReset,
    initialReplyBody = '',
    initialReplySubject = '',
    savingContact = false,
    deletingThread = false,
    contactError = '',
    // set when this thread's person already exists and the viewer is not a company admin: editing existing company contacts is admin-only
    contactLockedNotice = '',
    // set when the person is a new needs-review contact and the viewer is not an admin: only the names can be edited
    contactNameOnlyNotice = '',
    theme = 'light',
    sendingIdentities = [],
    companyName = '',
    showCompanyName = false,
    templates = [],
    // Comms gate fixes: the brand THIS conversation belongs to, for a template's "Company name" (never a page-wide default).
    templateCompanyName = '',
    allSessions = [],
    conversationIdleMinutes = 30,
    reconnectionMinutes = 15,
    // Comms final pass: pull to refresh on touch devices (reloads this conversation in place) and the Gated Group Communications
    // events ("Val took this conversation", "Released by Val") shown as system lines in the thread.
    onPullRefresh = null,
    gateEvents = [],
    voicemailStateByKey = {},
    onUpdateVoicemailState,
    voicemailStateError = '',
}) {
    const dark = theme === 'dark';
    const [, tickDeliveryClock] = useState(0);
    useEffect(() => { const timer = setInterval(() => tickDeliveryClock(n => n + 1), 30000); return () => clearInterval(timer); }, []);
    const viewport = useCommsViewport();
    const phone = viewport === 'phone';
    // phones and tablets share the compact header (main action + "⋯"); only phones get "← Back" (the tablet keeps its two panes)
    const compactHeader = viewport !== 'desktop';
    // BL-240 Gate 2: replies just sent by this staff member, held only until the next poll
    // returns the server's own conversation_log entry for them.
    const [pendingReplies, setPendingReplies] = useState([]);
    // BL-177 Step 3: scheduled messages for this conversation (purple dashed bubbles with live Edit / Send now / Cancel).
    const { rows: scheduledRows, error: scheduledLoadError, reload: reloadScheduled, accept: acceptScheduled } = useScheduledMessages({ client: supabaseClient, sessionId: intakeSession?.id || null, enabled: Boolean(intakeSession?.id) });
    const isCompanyAdmin = useIsCompanyAdmin(supabaseClient);
    // BL-177 Step 2o: "Reset test contact" shows ONLY for a company admin on a conversation whose phone or email is on the AI Test list (or is a
    // listed test identity). The database makes the call; any other thread never shows the item.
    const [resettableAddress, setResettableAddress] = useState(null);
    const [resetOpen, setResetOpen] = useState(false);
    const resetResultRef = useRef(null);
    const threadPhone = intakeSession?.contact_phone || '';
    const threadEmail = intakeSession?.contact_email || '';
    useEffect(() => {
        let live = true;
        setResettableAddress(null);
        if (!isCompanyAdmin) return undefined;
        (async () => {
            for (const address of [threadPhone, threadEmail]) {
                if (address && await canResetTestContact(supabaseClient, address)) { if (live) setResettableAddress(address); return; }
            }
        })();
        return () => { live = false; };
    }, [isCompanyAdmin, threadPhone, threadEmail]);
    // BL-177 Step 2o: why the AI stayed quiet on a text, as a small grey line under it.
    const aiNotes = useAiThreadNotes({
        client: supabaseClient,
        sessionId: intakeSession?.id || null,
        refreshKey: `${Array.isArray(intakeSession?.raw_message_log) ? intakeSession.raw_message_log.length : 0}:${intakeSession?.ai_paused_until || ''}`,
    });
    const [scheduledBusy, setScheduledBusy] = useState('');
    const [scheduledErrors, setScheduledErrors] = useState({});
    const [editingScheduled, setEditingScheduled] = useState(null);
    const [editScheduledError, setEditScheduledError] = useState('');
    const [openChecklistBlockId, setOpenChecklistBlockId] = useState(null);
    const [editingContact, setEditingContact] = useState(false);
    const [addingToCrm, setAddingToCrm] = useState(false);
    const [internalNotesOpen, setInternalNotesOpen] = useState(false);
    const [manualNote, setManualNote] = useState('');
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelPinned, setSidePanelPinned] = useState(false);
    const [templatePanelMode, setTemplatePanelMode] = useState('more');
    const [templatePanelChannel, setTemplatePanelChannel] = useState('');
    const [sidePanelTab, setSidePanelTab] = useState('ai_activity');
    const [selectedTemplateSid, setSelectedTemplateSid] = useState('');
    const [templateVariables, setTemplateVariables] = useState({});
    const [activeSendingIdentity, setActiveSendingIdentity] = useState(null);
    const [activeReplyChannel, setActiveReplyChannel] = useState(intakeSession?.channel || 'sms');
    const [expandedNoteIds, setExpandedNoteIds] = useState({});
    const [expandedVoicemailKeys, setExpandedVoicemailKeys] = useState({});
    const [noteFocusRequest, setNoteFocusRequest] = useState(0);
    const noteInputRef = useRef(null);
    const messageScrollRef = useRef(null);
    const [signedMediaUrlsByKey, setSignedMediaUrlsByKey] = useState({});
    const [mediaSignErrorsByKey, setMediaSignErrorsByKey] = useState({});
    const [mediaRenderErrorsByKey, setMediaRenderErrorsByKey] = useState({});
    const markedReadRef = useRef('');
    const contactErrorRef = useRef(null);
    useEffect(() => { if (contactError) contactErrorRef.current?.scrollIntoView?.({ block:'nearest' }); }, [contactError]);
    const [contactForm, setContactForm] = useState(() => contactFormFromThread(intakeSession, contact, account));
    const sessionId = intakeSession?.id;
    const [emailMoveFolders,setEmailMoveFolders] = useState(['Inbox','Sent']);
    const emailProviderId = intakeSession?.email_provider_id;
    useEffect(() => {
        let active=true;
        setEmailMoveFolders(['Inbox','Sent']);
        if (emailProviderId) (async()=>{
            try {
                const result=await supabaseClient.from('org_email_providers').select('folder_sync').eq('id',emailProviderId).maybeSingle();
                if (active && !result.error && result.data) setEmailMoveFolders(syncFolders(result.data));
            } catch { /* Keep the safe default folder choices when settings cannot be loaded. */ }
        })();
        return ()=>{active=false;};
    },[emailProviderId]);


    useEffect(() => {
        setPendingReplies([]);
        setOpenChecklistBlockId(null);
        setEditingContact(false);
        setAddingToCrm(false);
        setInternalNotesOpen(false);
        setManualNote('');
        setActiveSendingIdentity(null);
        markedReadRef.current = '';
    }, [sessionId]);

    // Declared after the per-thread reset above, so a request for this thread wins over that reset in the same commit.
    const contactEditorRequestNonce = openContactEditorRequest?.sessionId === sessionId ? openContactEditorRequest?.nonce : null;
    useEffect(() => {
        if (contactEditorRequestNonce === null || contactEditorRequestNonce === undefined) return;
        setContactForm(contactFormFromThread(intakeSession, contact, account));
        setAddingToCrm(true);
        setEditingContact(true);
        // Only a new request opens it; the thread's later updates must not re-open a form the person closed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactEditorRequestNonce]);

    useEffect(() => {
        if (editingContact) return;
        setContactForm(contactFormFromThread(intakeSession, contact, account));
    }, [account, contact, editingContact, intakeSession]);

    useEffect(() => {
        if (!currentUserId) return;
        let active = true;
        const loadPreference = async (key) => {
            const prefsQuery = supabaseClient?.from?.('user_preferences');
            if (!prefsQuery?.select) return null;
            const chain = prefsQuery
                .select('value')
                .eq('user_id', currentUserId)
                .eq('key', key);
            if (!chain?.maybeSingle) return null;
            const { data } = await chain.maybeSingle();
            return data?.value;
        };
        Promise.all([
            loadPreference('communications-side-panel-pinned'),
            loadPreference('communications-side-panel-last-tab'),
        ])
            .then(([pinned, tab]) => {
                if (!active) return;
                setSidePanelPinned(pinned === true || pinned === 'true');
                if (typeof tab === 'string' && ['ai_activity', 'templates', 'contact', 'trip'].includes(tab)) {
                    setSidePanelTab(tab);
                }
            })
            .catch(() => {});
        return () => { active = false; };
    }, [currentUserId]);

    const savePanelPreference = useCallback((key, value) => {
        if (!currentUserId) return;
        const prefsQuery = supabaseClient?.from?.('user_preferences');
        if (!prefsQuery?.upsert) return;
        prefsQuery
            .upsert({ user_id: currentUserId, key, value, updated_at: new Date().toISOString() }, { onConflict: 'user_id,key' })
            .then(undefined, () => {}); // Supabase query builders have no .catch(); a failed preference save must never crash the thread
    }, [currentUserId]);

    // BL-177 Step 2c: Retry for an imported provider text that was definitely not sent. The server marks the failed text as retried
    // and records the new one in one step, so a second click (or tab) can never send it twice.
    const [retryingRef, setRetryingRef] = useState('');
    const [retryError, setRetryError] = useState('');
    const retryFailedText = async (item) => {
        if (retryingRef || !item?.clientRef) return;
        setRetryingRef(item.clientRef);
        setRetryError('');
        try {
            await sendStaffReply({ session: intakeSession, body: item.text, channel: 'sms', retryClientRef: item.clientRef });
            if (typeof onReplySent === 'function') onReplySent({ client_ref: item.clientRef });
        } catch (failure) {
            setRetryError(failure.message || 'The text could not be retried.');
            if (typeof onReplySent === 'function') onReplySent({});
        } finally {
            setRetryingRef('');
        }
    };

    // BL-177 Step 2d (live bug): once the saved row for a just-sent text is here (same client_ref), the optimistic
    // copy is no longer needed. The thread already shows one bubble either way; this only keeps local state tidy.
    useEffect(() => {
        const saved = [
            ...(Array.isArray(intakeSession?.raw_message_log) ? intakeSession.raw_message_log : []),
            ...(Array.isArray(intakeSession?.conversation_log) ? intakeSession.conversation_log : []),
        ];
        setPendingReplies((current) => {
            const remaining = current.filter((entry) => !isReconciled(entry, saved));
            return remaining.length === current.length ? current : remaining;
        });
    }, [intakeSession?.raw_message_log, intakeSession?.conversation_log]);

    const handleSent = (entry) => {
        setPendingReplies((current) => [...current, entry]);
        if (!sidePanelPinned) setSidePanelOpen(false);
        if (typeof onReplySent === 'function') onReplySent(entry);
    };

    // BL-177 Step 3: the buttons on a scheduled bubble. Each reloads the thread's scheduled messages when done; a refusal (it already went
    // out, someone cancelled it first) is shown right on that bubble.
    const runScheduledAction = async (item, action) => {
        const id = item?.scheduledRow?.id;
        if (!id || scheduledBusy) return;
        setScheduledBusy(id);
        setScheduledErrors((current) => ({ ...current, [id]: '' }));
        try {
            await action(id);
        } catch (failure) {
            setScheduledErrors((current) => ({ ...current, [id]: failure?.message || 'That could not be done.' }));
        } finally {
            setScheduledBusy('');
            reloadScheduled();
            if (typeof onReplySent === 'function') onReplySent({});
        }
    };
    const scheduledControlsFor = (item) => {
        const row = item?.scheduledRow;
        if (!row || (item.scheduled !== true && item.scheduled_cancelled !== true)) return null;
        const open = row.status === 'scheduled';
        return {
            canEdit: open && canEditScheduled(row, { userId: currentUserId }),
            canCancel: open && canManageScheduled(row, { userId: currentUserId, isAdmin: isCompanyAdmin }),
            busy: scheduledBusy === row.id || row.status === 'sending',
            error: scheduledErrors[row.id] || '',
            onEdit: () => { setEditScheduledError(''); setEditingScheduled(row); },
            onSendNow: (target) => runScheduledAction(target, (id) => sendScheduledNow(id)),
            onCancel: (target) => runScheduledAction(target, (id) => cancelScheduledMessage(id)),
            loadEvents: (id) => loadScheduledEvents(id, supabaseClient),
        };
    };
    const saveScheduledEdit = async ({ body, at, cancelOnReply }) => {
        if (!editingScheduled) return false;
        setScheduledBusy(editingScheduled.id);
        setEditScheduledError('');
        try {
            await updateScheduledMessage(editingScheduled.id, { ...(body !== undefined ? { body } : {}), scheduledFor: at, cancelOnReply }, supabaseClient);
            setEditingScheduled(null);
            reloadScheduled();
            return true;
        } catch (failure) {
            setEditScheduledError(failure?.message || 'The change could not be saved.');
            return false;
        } finally {
            setScheduledBusy('');
        }
    };

    const internalNotes = useMemo(() => normalizeInternalNotes(intakeSession?.internal_notes), [intakeSession?.internal_notes]);
    const displayedSession = useMemo(() => visibleEmailThreadSession(intakeSession, allSessions), [intakeSession, allSessions]);
    const items = useMemo(() => buildThreadItems({
        conversationLog: displayedSession?.conversation_log,
        rawMessageLog: displayedSession?.raw_message_log,
        pendingReplies,
        gapMs: Math.max(1, conversationIdleMinutes) * 60 * 1000,
    }), [conversationIdleMinutes, displayedSession, pendingReplies]);
    const receiptHistory = useMemo(() => [...items, ...allSessions.filter(row => row.org_id === intakeSession?.org_id).flatMap(row =>
        (Array.isArray(row.raw_message_log) ? row.raw_message_log : []).map(entry => ({ direction: entry.direction, channel: entry.channel,
            sendingLine: entry.sending_line || entry.from, carrier: entry.carrier, provider_status: entry.provider_status })))], [items, allSessions, intakeSession?.org_id]);
    // The progress word (Queued / Delivered / Read) belongs to the conversation's most recent outbound message only; a newer
    // outbound message takes it over on the next render (no reload).
    const threadItems = useMemo(
        () => mergeGateEvents([...items, ...buildScheduledThreadItems(scheduledRows, items)], gateEvents),
        [gateEvents, items, scheduledRows],
    );
    const lastMessageAt = useMemo(
        () => [...items].reverse().find((item) => item?.at && !item?.systemLine)?.at || null,
        [items]
    );
    const lastMessageLabel = formatEasternTimestamp(lastMessageAt);
    const latestOutboundId = useMemo(() => {
        // A scheduled text has not been sent yet, so it never takes the progress word from the one that has.
        const outboundItems = threadItems.filter((item) => item?.direction === 'outbound' && item?.kind !== 'divider' && messageDeliveryState(item) !== 'scheduled');
        return outboundItems.length ? outboundItems[outboundItems.length - 1].id : null;
    }, [threadItems]);
    // BL-177 Gate 17b: the message history shows real messages only. Internal notes (staff notes
    // and status events) live in their own panel under the reply box.
    const blocks = useMemo(
        () => buildConversationBlocks(groupEmailTimeline(threadItems)).map((block) => ({ ...block, timeline: block.messages })),
        [threadItems]
    );
    const notesNewestFirst = useMemo(
        () => [...internalNotes].sort((left, right) => (toTimestamp(right.createdAt) ?? 0) - (toTimestamp(left.createdAt) ?? 0)),
        [internalNotes]
    );
    const storageMedia = useMemo(() => {
        const entries = [];
        const seen = new Set();
        items.forEach((item) => {
            if (item?.kind !== 'message') return;
            (Array.isArray(item.media) ? item.media : []).forEach((media) => {
                if (!media?.storagePath || !media?.key || seen.has(media.key)) return;
                seen.add(media.key);
                entries.push(media);
            });
        });
        return entries;
    }, [items]);
    const checklistHasAttribution = useMemo(() => hasChecklistAttribution(intakeSession?.capture_checklist), [intakeSession?.capture_checklist]);
    const unreadInternalNotes = useMemo(
        () => countUnreadInternalNotes(intakeSession, currentUserId),
        [currentUserId, intakeSession]
    );
    // BL-177 Gate 17c: a Closed conversation goes quiet — every alert in the header and the notes
    // bar turns grey until it is reopened. Archived conversations keep their own look.
    const closedQuiet = isConversationClosed(intakeSession);
    // 1.12.212-beta: what kind of line the client's number is, and whether it is on WhatsApp (checked once, cached).
    const phoneChannel = usePhoneCapability(supabaseClient, canCallConversation(intakeSession) ? intakeSession?.contact_phone : '');
    // 1.12.212-beta: the thread's email subject — the newest email message that carries one (received or sent), else the subject
    // the inbound webhook stored on the conversation itself (emails received before this release only have that one).
    const lastEmailSubject = useMemo(() => {
        const entries = [
            ...(Array.isArray(intakeSession?.raw_message_log) ? intakeSession.raw_message_log : []),
            ...(Array.isArray(intakeSession?.conversation_log) ? intakeSession.conversation_log : []),
        ];
        const withSubject = entries
            .filter((entry) => entry?.channel === 'email' && String(entry.subject || entry.email_subject || '').trim())
            .sort((left, right) => new Date(right.sent_at || right.received_at || right.at || 0).getTime() - new Date(left.sent_at || left.received_at || left.at || 0).getTime());
        const newest = withSubject[0];
        return String(newest?.subject || newest?.email_subject || intakeSession?.email_subject || '').trim();
    }, [intakeSession?.raw_message_log, intakeSession?.conversation_log, intakeSession?.email_subject]);
    const threadEmailSubject = lastEmailSubject.replace(/^((re|fw|fwd):\s*)+/i, '');

    // BL-177 Step 2j (B3): scroll to the latest message only when the person is at (within ~80 px of) the bottom, or sent it themselves.
    const threadScroll = useThreadScroll({ paneRef: messageScrollRef, items, sessionId: intakeSession?.id || null });
    const conversationPull = usePullToRefresh(messageScrollRef, {
        onRefresh: async () => {
            reloadScheduled?.();
            await onPullRefresh?.();
        },
        enabled: typeof onPullRefresh === 'function',
    });

    // The header "Note" button opens the panel and puts the cursor in the note input.
    useEffect(() => {
        if (!noteFocusRequest || !internalNotesOpen) return;
        noteInputRef.current?.focus();
    }, [internalNotesOpen, noteFocusRequest]);

    useEffect(() => {
        if (!internalNotesOpen || !sessionId) {
            markedReadRef.current = '';
            return;
        }
        const markKey = `${sessionId}:${internalNotes.length}`;
        if (markedReadRef.current === markKey) return;
        markedReadRef.current = markKey;
        onMarkInternalNotesRead?.(sessionId);
    }, [internalNotes.length, internalNotesOpen, onMarkInternalNotesRead, sessionId]);

    useEffect(() => {
        let active = true;
        if (storageMedia.length === 0) {
            setSignedMediaUrlsByKey({});
            setMediaSignErrorsByKey({});
            return () => { active = false; };
        }
        Promise.all(storageMedia.map(async (media) => {
            const bucket = media.storageBucket || INBOUND_MESSAGE_MEDIA_BUCKET;
            try {
                const { data, error } = await supabaseClient.storage
                    .from(bucket)
                    .createSignedUrl(media.storagePath, 3600);
                if (error || !data?.signedUrl) {
                    return { key: media.key, signedUrl: '', failed: true };
                }
                return { key: media.key, signedUrl: data.signedUrl, failed: false };
            } catch {
                return { key: media.key, signedUrl: '', failed: true };
            }
        })).then((results) => {
            if (!active) return;
            const nextUrls = {};
            const nextErrors = {};
            results.forEach((result) => {
                if (result.failed) {
                    nextErrors[result.key] = true;
                    return;
                }
                nextUrls[result.key] = result.signedUrl;
            });
            setSignedMediaUrlsByKey(nextUrls);
            setMediaSignErrorsByKey(nextErrors);
        });
        return () => { active = false; };
    }, [storageMedia]);

    useEffect(() => {
        setMediaRenderErrorsByKey({});
    }, [items]);

    const contactName = resolveContactName(intakeSession, account, contact, contact ? null : myContact);
    const address = resolveContactAddress(intakeSession);
    const headerName = threadDisplayName({ name: contactName, session: intakeSession }, { participantNames });
    const savedName = firstString(contact?.name, account?.account_name, myContact?.name);
    const savedNameIsAddress = savedName === intakeSession?.contact_phone
        || savedName === intakeSession?.contact_email
        || /^\+?[\d\s().-]{3,}$/.test(savedName);
    const headerSubtitle = threadHeaderSubtitle({
        session: intakeSession,
        companyName: showCompanyName ? companyName : '',
        showAddress: Boolean(savedName && !savedNameIsAddress && intakeSession?.channel !== 'email' && (contact?.phones || []).filter((p) => typeof p === 'string' || !p.type || ['mobile', 'cell', 'cellular'].includes(p.type)).length > 1),
    });
    const headerFlag = threadFlag(intakeSession);
    const pauseUntil = intakeSession?.ai_paused_until ? new Date(intakeSession.ai_paused_until) : null;
    const pausedUntilClosed = (
        !pauseUntil
        &&
        intakeSession?.ai_pause_reason === AI_PAUSE_UNTIL_CLOSED_REASON
        && isAiThreadOpen(intakeSession)
    );
    const aiPaused = pausedUntilClosed || Boolean(
        pauseUntil
        && !Number.isNaN(pauseUntil.getTime())
        && pauseUntil > new Date()
    );
    const latestStaffEntry = [...(Array.isArray(intakeSession?.conversation_log) ? intakeSession.conversation_log : [])]
        .reverse()
        .find((entry) => entry?.role === 'staff');
    const pausedById = intakeSession?.ai_paused_by || latestStaffEntry?.staffUserId || null;
    // BL-177 Step 2o: say who or what paused it. A person: their name. A text typed in the imported provider app has no IntelAgent user behind it.
    const isTakeoverReason = intakeSession?.ai_pause_reason === 'staff_takeover' || pausedUntilClosed;
    const pausedByName = pausedById
        ? (staffNames[pausedById] || 'Staff')
        : (isTakeoverReason ? 'staff (an imported message)' : 'a pause rule');
    const resumeLabel = pausedUntilClosed ? 'this thread closes' : formatStamp(pauseUntil?.getTime());
    const filledEmails = (contactForm.emails || []).map(row => (row.value || '').trim()).filter(Boolean);
    const filledPhones = (contactForm.phones || []).map(row => (row.value || '').trim()).filter(Boolean);
    const editEmailError = filledEmails.some(value => !EMAIL_RE.test(value))
        ? 'Enter a valid email address.'
        : '';
    const editPhoneError = filledPhones.some(value => !normalizeOutboundPhoneAddress(value).startsWith('+'))
        ? 'Enter a complete phone number.'
        : '';
    // A Business contact with no company is NOT a reason to disable Save: the form says what is missing on click (item 9).
    const canSaveContact = Boolean(
        contactForm.name.trim()
        && (filledEmails.length || filledPhones.length)
        && !editEmailError
        && !editPhoneError
        && !contactLockedNotice
    );
    // Keep only a chat-specific difference in this header; company AI status stays in the global status control.
    const conversationAiStatus = describeConversationAiStatus({
        settings: aiPauseSettings, session: intakeSession, pausedByName, resumeLabel,
    });
    const aiPauseControl = conversationAiStatus?.state === 'thread-paused' && (
        <div className={`ia-ai-paused-note-group ${dark ? 'is-dark' : ''}`}>
            <span
                className={`ia-ai-paused-note ${dark ? 'is-dark' : ''}`}
                role="status"
                aria-label="AI paused for this chat"
                data-testid="ai-paused-note"
            >
                AI paused for this chat
            </span>
            {aiPaused && onHandBackToAi && (
                <SupportWriteGuard mode="disable" tooltip="Resuming the AI is disabled in a read-only support session">
                    <button type="button" onClick={onHandBackToAi} className="ia-ai-pause-resume" data-testid="resume-chat-ai">
                        Resume AI
                    </button>
                </SupportWriteGuard>
            )}
        </div>
    );

    // BL-177 Step 2d: navy card, white text; only timestamps are dim.
    const cardCls = dark ? 'bg-[#111B30] border-[#26365A] text-white' : 'bg-white border-gray-200 text-gray-800';
    const labelCls = dark ? 'text-white' : 'text-gray-600';
    const dividerCls = dark ? 'border-[#26365A]' : 'border-gray-100';
    const [appChatReadAt, setAppChatReadAt] = useState(null);
    useEffect(() => {
        if (intakeSession?.channel !== 'app_chat' || !intakeSession?.account_id) return undefined;
        let active = true;
        let marked = '';
        const refresh = async () => {
            if (document.visibilityState === 'hidden') return;
            try {
                const data = await chatRpc(supabaseClient, 'm9_chat_read', { p_account: intakeSession.account_id, p_session: intakeSession.id });
                if (!active) return;
                setAppChatReadAt(data.peerReadAt);
                const last = data.messages?.at(-1);
                if (last && marked !== last.id) {
                    await chatRpc(supabaseClient, 'm9_chat_mark_read', { p_account: intakeSession.account_id, p_session: intakeSession.id, p_through: last.id });
                    marked = last.id;
                }
            } catch { /* Existing inbox error/reload handles unavailable content; do not claim read. */ }
        };
        refresh(); const stop = watchChat(supabaseClient, intakeSession.account_id, refresh);
        const timer = setInterval(refresh, 5000);
        document.addEventListener('visibilitychange', refresh);
        return () => { active = false; stop(); clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
    }, [intakeSession?.id, intakeSession?.channel, intakeSession?.account_id]);
    const accessMode = access?.mode || 'company';
    const readOnly = accessMode === 'colleague';
    // Gated Group Communications: who owns this conversation, said plainly in the header.
    const gateOwnerLabel = access?.gated && access?.ownerName
        ? (access.iAmOwner ? 'You own this conversation' : `Owned by ${access.ownerName}`)
        : '';
    const handlerName = access?.readOnlyBy || 'A colleague';
    const iconButtonCls = `inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm leading-none focus:outline-none focus:ring-1 focus:ring-[var(--comms-flag-followup)] ${dark ? 'text-amber-200 hover:bg-gray-700 focus:bg-gray-700' : 'text-amber-700 hover:bg-gray-50 focus:bg-gray-50'}`;

    const updateContactName = (value, parts) => {
        setContactForm((current) => ({
            ...current,
            name: value,
            ...(parts ? { firstName: parts.firstName.trim(), lastName: parts.lastName.trim() } : {}),
            // a Personal account is named after the person (first + last name)
            accountName: current.accountKind === 'personal' ? personalAccountNameOf(value) : current.accountName,
        }));
    };

    const updateAccountKind = (kind) => {
        setContactForm((current) => ({
            ...current,
            accountKind: kind,
            individual: kind === 'personal',
            accountName: kind === 'personal' ? personalAccountNameOf(current.name) : current.accountName,
        }));
    };

    const saveContact = async () => {
        if (!canSaveContact || typeof onSaveContact !== 'function') return;
        if (contactForm.accountKind !== 'personal' && !contactForm.accountName.trim()) return;   // the form shows the inline error
        const phones = (contactForm.phones || []).map(row => ({
            ...row, value: normalizeOutboundPhoneAddress(row.value) || (row.value || '').trim(),
        }));
        const emails = (contactForm.emails || []).map(row => ({ ...row, value: (row.value || '').trim() }));
        const saved = await onSaveContact({
            ...contactForm,
            phones,
            emails,
            phone: primaryEntry(phones)?.value || '',
            email: primaryEntry(emails)?.value || '',
        });
        if (saved !== false) { setEditingContact(false); setAddingToCrm(false); }
    };

    const deleteThread = () => {
        if (typeof onDeleteThread !== 'function') return;
        if (window.confirm('Permanently delete this conversation thread and all of its messages? This cannot be undone.')) {
            onDeleteThread();
        }
    };

    const addManualInternalNote = async () => {
        const note = manualNote.trim();
        if (!note || typeof onAddInternalNote !== 'function') return;
        const saved = await onAddInternalNote(sessionId, note);
        if (saved !== false) setManualNote('');
    };

    const focusNoteInput = () => {
        setInternalNotesOpen(true);
        setNoteFocusRequest((current) => current + 1);
    };

    const applyHeaderFlag = () => {
        if (typeof onSetFlag === 'function') {
            onSetFlag({ status: 'follow_up', urgency: null, active: true, note: '' });
        }
        setInternalNotesOpen(true);
    };

    const openTemplatesPanel = useCallback(() => {
        setSidePanelOpen(true);
        setSidePanelTab('templates');
        savePanelPreference('communications-side-panel-last-tab', 'templates');
    }, [savePanelPreference]);

    const handleTemplatePanelRequestOpen = useCallback((mode = 'more', channel = '') => {
        setTemplatePanelMode(mode); setTemplatePanelChannel(channel);
        openTemplatesPanel();
    }, [openTemplatesPanel]);

    const handlePanelTabChange = useCallback((nextTab) => {
        setTemplatePanelMode('more');
        setSidePanelTab(nextTab);
        savePanelPreference('communications-side-panel-last-tab', nextTab);
    }, [savePanelPreference]);

    // BL-177 Step 2j (B1): the moment an invite is sent, the right-hand panel opens on People so the person sees who was invited,
    // their note and what the colleague will see. An invite that was already waiting when the conversation was opened does not re-open it.
    const pendingInviteId = access?.pendingInvite?.id || null;
    const seenInviteRef = useRef({ sessionId: null, inviteId: null });
    useEffect(() => {
        const seen = seenInviteRef.current;
        if (seen.sessionId !== sessionId) { seenInviteRef.current = { sessionId, inviteId: pendingInviteId }; return; }
        if (pendingInviteId && seen.inviteId !== pendingInviteId) {
            seenInviteRef.current = { sessionId, inviteId: pendingInviteId };
            handlePanelTabChange('people');
            setSidePanelOpen(true);
        } else if (!pendingInviteId) {
            seenInviteRef.current = { sessionId, inviteId: null };
        }
    }, [pendingInviteId, sessionId, handlePanelTabChange]);

    const handleTogglePinnedPanel = useCallback(() => {
        setSidePanelPinned((current) => {
            const next = !current;
            savePanelPreference('communications-side-panel-pinned', next);
            return next;
        });
    }, [savePanelPreference]);

    // BL-177 Step 2n (phones): the header keeps ONE main action beside "← Back" (Take, or Join on a colleague's conversation); every
    // other action sits in the "⋯" menu. Same handlers as the wide header.
    const openDetailsPanel = () => {
        handlePanelTabChange(accessMode === 'mine' ? 'people' : 'contact');
        setSidePanelOpen(true);
    };
    // Comms gate fixes: exactly one Release on phones and tablets. The owner ("mine") gets it as the main action; an admin reading a
    // colleague's owned conversation gets it in the "⋯" menu (the main action stays Request to join).
    const takeIsMain = accessMode === 'company' && Boolean(access?.onTake);
    const releaseIsMain = !takeIsMain && Boolean(access?.canRelease && access?.onRelease) && accessMode === 'mine';
    const releaseInMenu = !releaseIsMain && Boolean(access?.canRelease && access?.onRelease);
    const phoneMainAction = takeIsMain ? (
        <SupportWriteGuard mode="disable" tooltip="Taking conversations is disabled in a read-only support session">
            <button type="button" onClick={access.onTake} disabled={access.busy} data-access-action="take" className="ia-btn3d ia-btn3d--go ia-comms-pill">
                Take
            </button>
        </SupportWriteGuard>
    ) : (releaseIsMain ? (
        <SupportWriteGuard mode="disable" tooltip="Releasing conversations is disabled in a read-only support session">
            <button type="button" onClick={access.onRelease} disabled={access.busy} data-access-action="release" title="Put this conversation back in the Company inbox" className="ia-btn3d ia-comms-pill">
                Release
            </button>
        </SupportWriteGuard>
    ) : (readOnly && access?.onJoin ? (
        <SupportWriteGuard mode="disable" tooltip="Join requests are disabled in a read-only support session">
            <button type="button" onClick={access.onJoin} disabled={access.busy || access.joinPending} data-access-action="join" className="ia-btn3d ia-comms-pill">
                {access.joinPending ? 'Join requested' : (access.gated ? 'Request to join' : 'Join')}
            </button>
        </SupportWriteGuard>
    ) : null));
    const menuItemCls = 'ia-btn3d ia-comms-pill ia-thread-actions__item';
    // Click-to-call: only a one-to-one conversation with a real phone number can be called.
    const callable = canCallConversation(intakeSession);
    const phoneMenuItems = (
        <>
            {releaseInMenu && (
                <SupportWriteGuard mode="disable" tooltip="Releasing conversations is disabled in a read-only support session">
                    <button type="button" role="menuitem" className={menuItemCls} onClick={access.onRelease} disabled={access.busy} data-access-action="release" data-thread-menu-item="release" title="Put this conversation back in the Company inbox">
                        Release
                    </button>
                </SupportWriteGuard>
            )}
            <button type="button" role="menuitem" className={menuItemCls} onClick={focusNoteInput} aria-label={unreadInternalNotes > 0 ? `Add internal note (${unreadInternalNotes} unread)` : 'Add internal note'} data-thread-menu-item="note">
                {unreadInternalNotes > 0 ? `Note · ${unreadInternalNotes}` : 'Note'}
            </button>
            <button type="button" role="menuitem" className={menuItemCls} onClick={applyHeaderFlag} data-thread-menu-item="flag">Flag</button>
            {callable && (
                <ClickToCallButton
                    client={supabaseClient}
                    sessionId={intakeSession.id}
                    fallbackTel={intakeSession.contact_phone}
                    disabled={readOnly}
                    disabledReason={readOnly ? `${access?.readOnlyBy || 'A colleague'} is handling this conversation` : ''}
                    className={menuItemCls}
                    menuItem
                />
            )}
            <CreateReservationButton
                client={supabaseClient}
                userId={currentUserId} accounts={account?[account]:[]}
                sessionId={intakeSession?.id || null}
                disabled={readOnly}
                disabledReason={readOnly ? `${access?.readOnlyBy || 'A colleague'} is handling this conversation` : ''}
                onChanged={onReservationChanged}
                className={menuItemCls}
                menuItem
            />
            {onToggleArchive && (
                <button type="button" role="menuitem" className={menuItemCls} onClick={onToggleArchive} data-thread-menu-item="archive">
                    {intakeSession?.archived_at ? 'Unarchive' : 'Archive'}
                </button>
            )}
            {resettableAddress && (
                <button type="button" role="menuitem" className={menuItemCls} onClick={() => setResetOpen(true)} data-thread-menu-item="reset-test-contact">
                    Reset test contact
                </button>
            )}
            <button type="button" role="menuitem" className={menuItemCls} onClick={openDetailsPanel} data-thread-menu-item="details">
                {accessMode === 'mine' ? 'People & details' : 'Details'}
            </button>
        </>
    );

    return (
        <div className={`h-full min-h-0 flex flex-col rounded-xl border ${cardCls}`} data-closed-quiet={closedQuiet ? 'true' : undefined}>
            <div className={`flex min-w-0 flex-col gap-2 p-4 border-b ${dividerCls}`} data-thread-header-bar="true" data-thread-header-layout={compactHeader ? 'compact' : 'wide'}>
                {scheduledLoadError && <p role="alert">{scheduledLoadError}</p>}
                {phone && onBack && <button type="button" onClick={onBack} className="ia-btn3d ia-comms-pill ia-thread-back" aria-label="Back to conversations" data-thread-back="true"><span aria-hidden="true">←</span> Back</button>}
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                {/* BL-177 Step 2d: the name + number · company keep at least ~14rem; when the actions don't fit beside them
                    they wrap underneath instead of squeezing the identity to nothing. */}
                <div className="flex min-w-[min(14rem,100%)] flex-[1_1_14rem] items-center gap-3" data-thread-header-identity="true">
                    <span aria-hidden="true" className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold bg-[#22304F] text-white">
                        {threadAvatarLabel({ name: contactName, session: intakeSession })}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <h3 className="min-w-0 truncate text-lg font-bold text-white">{headerName}</h3><ResponsiveThreadActions main={phoneMainAction}>{phoneMenuItems.props.children}</ResponsiveThreadActions>
                            {!phone && onClose && <button type="button" aria-label="Close conversation" className="ia-btn3d ia-comms-pill" onClick={onClose}>✕</button>}
                            <SupportWriteGuard mode="disable" tooltip="Editing contacts is disabled in a read-only support session">
                                <button
                                    type="button"
                                    onClick={() => { setAddingToCrm(false); setEditingContact(true); }}
                                    className={iconButtonCls}
                                    aria-label="Edit contact"
                                    title="Edit contact"
                                >
                                    ✏️
                                </button>
                            </SupportWriteGuard>
                        </div>
                        {String(intakeSession?.external_thread_id || '').startsWith('conversations:') && <GroupParticipantsControl key={intakeSession.id} client={supabaseClient} session={intakeSession} names={participantNames} readOnly={readOnly} />}
                        {headerSubtitle && (
                            <p className="text-sm mt-0.5 truncate text-white" data-thread-header-subtitle="true">
                                {headerSubtitle}
                            </p>
                        )}
                        {phoneChannel.phone && intakeSession?.channel !== 'email' && (
                            <p className="mt-1 text-xs text-[var(--ink)]" data-phone-channel-result="true">
                                {activeReplyChannel === 'sms' && phoneChannel.capability?.line_type === 'landline' ? "Can't text this number (landline)"
                                    : activeReplyChannel === 'whatsapp' && phoneChannel.capability?.whatsapp_status === 'not_on' ? 'Not on WhatsApp' : ''}
                            </p>
                        )}
                        {contact?.sms_opted_out_at && (
                            <p className={`mt-1 text-xs font-medium ${closedQuiet ? QUIET_TEXT_CLASS : 'text-red-600'}`}>Opted out of texts</p>
                        )}
                    </div>
                </div>
                </div>
                {(
                    <div className="ia-thread-header-meta" data-thread-header-meta="true">
                        {aiPauseControl}
                        {gateOwnerLabel && (
                            <span className="ia-gate-owner" data-gate-owner="true" data-gate-owner-compact="true">{gateOwnerLabel}</span>
                        )}
                        {headerFlag && headerFlag.key !== 'none' && (
                            <StatusChip tone={headerFlag.tone}><span data-thread-header-flag={headerFlag.key}>{headerFlag.label}</span></StatusChip>
                        )}
                        <span className="text-xs text-white">
                            customer last wrote <span className="ia-d2-dim">{formatRelativeAge(intakeSession?.last_inbound_at || intakeSession?.updated_at)}</span>
                        </span>
                    </div>
                )}
            </div>

            {accessMode === 'mine' && (
                <ConversationPresenceBand count={access?.people?.length || 0} onOpen={() => { handlePanelTabChange('people'); setSidePanelOpen(true); }} />
            )}

            {editingContact && (
                <div className={`border-b p-4 ${dividerCls}`}>
                    {contactError && <p ref={contactErrorRef} className={`mb-3 text-xs ${dark ? 'text-red-300' : 'text-red-700'}`} role="alert">{contactError}</p>}
                    <ContactDetailsForm
                        dialogLabel={addingToCrm && !contact ? 'Add to CRM' : 'Edit contact'}
                        wrapperClassName={`rounded-lg border p-3 shadow-sm ${dark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}
                        intro={addingToCrm && !contact
                            ? 'This number or email is not in the CRM yet. Add a name to save it as a contact.'
                            : 'Update the CRM contact/account tied to this thread.'}
                        idPrefix="edit-contact"
                        name={contactForm.name}
                        accountName={contactForm.accountName}
                        accountKind={contactForm.accountKind}
                        emails={contactForm.emails}
                        phones={contactForm.phones}
                        readOnlyNotice={contactLockedNotice}
                        nameOnlyNotice={contactNameOnlyNotice}
                        contact={contact}
                        consentReadOnly={false}
                        emailError={editEmailError}
                        phoneError={editPhoneError}
                        canSubmit={canSaveContact}
                        submitting={savingContact}
                        submitLabel={addingToCrm && !contact ? 'Add to CRM' : 'Save contact'}
                        submitTooltip="Editing contacts is disabled in a read-only support session"
                        destructiveLabel={onDeleteThread ? 'Delete' : ''}
                        destructiveTooltip="Deleting conversations is disabled in a read-only support session"
                        destructiveDisabled={deletingThread}
                        onDestructive={onDeleteThread ? deleteThread : null}
                        onNameChange={updateContactName}
                        onAccountNameChange={(value) => setContactForm((current) => ({ ...current, accountName: value }))}
                        onAccountKindChange={updateAccountKind}
                        onEmailsChange={(rows) => setContactForm((current) => ({
                            ...current,
                            emails: rows,
                            email: primaryEntry(rows)?.value || '',
                        }))}
                        onPhonesChange={(rows) => setContactForm((current) => ({
                            ...current,
                            phones: rows,
                            phone: (primaryEntry(rows)?.value || '').trim(),
                        }))}
                        onCancel={() => { setEditingContact(false); setAddingToCrm(false); }}
                        onSubmit={saveContact}
                        theme={theme}
                    />
                </div>
            )}

            <div className="relative flex min-h-0 flex-1">
                <div className="min-w-0 flex-1 flex flex-col">
                    <div className="relative flex min-h-0 flex-1 flex-col" data-conversation-message-region="true">
                    <div ref={messageScrollRef} onScroll={threadScroll.onScroll} className="flex-1 min-h-0 overflow-auto p-4 pb-16 space-y-3" data-thread-scroll="true">
                        <PullToRefreshIndicator state={conversationPull} label="conversation" />
                        {voicemailStateError && (
                            <p role="alert" className="rounded-lg border border-red-400/40 bg-red-950/30 px-3 py-2 text-sm text-red-200">
                                {voicemailStateError}
                            </p>
                        )}
                        {items.length === 0 ? (
                            <p className={`text-sm text-center py-8 ${labelCls}`}>No messages in this conversation yet.</p>
                        ) : blocks.map((block, blockIndex) => {
                            const blockChecklist = checklistHasAttribution ? checklistForBlock(intakeSession?.capture_checklist, block) : intakeSession?.capture_checklist;
                            const blockHasChecklistAttribution = hasChecklistAttribution(blockChecklist);
                            return (
                                <React.Fragment key={block.id}>
                                    <div className="flex justify-center">
                                        {blockHasChecklistAttribution ? (
                                            <ConversationChecklistPill
                                                checklist={blockChecklist}
                                                blockId={block.id}
                                                isOpen={openChecklistBlockId === block.id}
                                                onToggle={(id) => setOpenChecklistBlockId((openId) => (openId === id ? null : id))}
                                                theme={theme}
                                            />
                                        ) : (blockIndex > 0 ? <ConversationEndMarker theme={theme} /> : null)}
                                    </div>
                                    {block.timeline.map((item) => {
                                        if (item.kind === 'email-thread') return <EmailThreadBlock key={item.id} messages={item.messages} folders={emailMoveFolders} subject={intakeSession?.email_subject} onAction={async (message, action, value) => {
                                            const result = await supabaseClient.functions.invoke('email-sync-action', { body: { sessionId: message.emailSessionId || intakeSession.id, messageId: message.emailSyncId, action, value } });
                                            if (result.error || result.data?.error) throw new Error(result.data?.error || result.error?.message || 'Mailbox action failed');
                                            onReplySent?.({});
                                        }} />;
                                        if (item.systemLine) {
                                            return (
                                                <div key={item.id} className="flex justify-center">
                                                    <MessageBubble item={item} theme={theme}>{item.text}</MessageBubble>
                                                </div>
                                            );
                                        }
                                        const outbound = item.direction === 'outbound';
                                        const aiNote = item.providerEventId ? aiNotes[item.providerEventId] : null;
                                        const voicemailKey = item.callSid
                                            ? voicemailStateKey(intakeSession?.id, item.callSid)
                                            : '';
                                        const voicemailState = voicemailKey ? voicemailStateByKey[voicemailKey] : null;
                                        if (item.callStatus === 'voicemail' && voicemailState?.deleted_at) return null;
                                        if (item.callStatus === 'voicemail' && voicemailState?.heard_at && !expandedVoicemailKeys[voicemailKey]) {
                                            return (
                                                <div key={item.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                                                    <button
                                                        type="button"
                                                        aria-label={`Expand heard voicemail, ${item.callDuration ?? 0} seconds`}
                                                        onClick={() => setExpandedVoicemailKeys((current) => ({ ...current, [voicemailKey]: true }))}
                                                        className="rounded-full border border-[#3B4D73] bg-[#16223B] px-3 py-1 text-left text-xs text-[#DCE5F2]"
                                                    >
                                                        Voicemail · {item.callDuration ?? 0}s · Heard
                                                    </button>
                                                </div>
                                            );
                                        }
                                        const markVoicemailHeard = () => onUpdateVoicemailState?.({
                                            sessionId: intakeSession?.id,
                                            callSid: item.callSid,
                                            changes: { heard_at: new Date().toISOString() },
                                        });
                                        return (
                                            <React.Fragment key={item.id}>
                                            <div className={`flex min-w-0 ${outbound ? 'justify-end' : 'justify-start'}`}>
                                                <div className="ia-bubble-wrap min-w-0 max-w-[80%]">
                                                    <MessageBubble
                                                    reconnectionMinutes={reconnectionMinutes} receiptHistory={receiptHistory}
                                                        item={item}
                                                        staffNames={staffNames}
                                                        participantNames={participantNames}
                                                        showProgress={item.id === latestOutboundId}
                                                        sessionChannel={intakeSession?.channel}
                                                        onRetry={retryFailedText}
                                                        retrying={retryingRef === item.clientRef}
                                                        retryDisabled={Boolean(retryingRef)}
                                                        scheduledControls={scheduledControlsFor(item)}
                                                    >
                                                        {highlightCapturedChecklistText(item.text, blockChecklist).map((segment, index) => (
                                                            segment.highlight ? (
                                                                <span key={`${item.id}-highlight-${index}`} className="rounded border border-[#4ADE80] bg-transparent px-0.5 text-white">
                                                                    {renderMessageText(segment.text, `${item.id}-highlight-${index}`)}
                                                                </span>
                                                            ) : <React.Fragment key={`${item.id}-plain-${index}`}>{renderMessageText(segment.text, `${item.id}-plain-${index}`)}</React.Fragment>
                                                        ))}
                                                        {item.channel === 'app_chat' && <AppChatMessageMeta item={item} peerReadAt={appChatReadAt} />}
                                                        {(Array.isArray(item.media) ? item.media : []).map((media) => {
                                                            const url = media?.url || signedMediaUrlsByKey[media?.key] || '';
                                                            const unavailable = Boolean(mediaSignErrorsByKey[media?.key] || mediaRenderErrorsByKey[media?.key]);
                                                            if (unavailable) {
                                                                return (
                                                                    <div key={media?.key} className="mt-2 rounded-lg border px-2 py-1 text-xs text-white">
                                                                        {media?.contentType?.startsWith('audio/') ? 'Voicemail audio unavailable' : 'Image unavailable'}
                                                                    </div>
                                                                );
                                                            }
                                                            if (!url) return null;
                                                            if (media?.contentType?.startsWith('audio/')) {
                                                                return (
                                                                    <audio
                                                                        key={media?.key}
                                                                        controls
                                                                        preload="none"
                                                                        src={url}
                                                                        aria-label="Voicemail recording"
                                                                        className="mt-2 w-full"
                                                                        onError={() => setMediaRenderErrorsByKey((current) => ({ ...current, [media.key]: true }))}
                                                                        onEnded={item.callStatus === 'voicemail' ? markVoicemailHeard : undefined}
                                                                    />
                                                                );
                                                            }
                                                            return (
                                                                <img
                                                                    key={media?.key}
                                                                    src={url}
                                                                    alt="Message attachment"
                                                                    className="mt-2 max-h-64 max-w-full rounded-lg object-contain"
                                                                    onError={() => setMediaRenderErrorsByKey((current) => ({ ...current, [media.key]: true }))}
                                                                />
                                                            );
                                                        })}
                                                        {item.callStatus === 'voicemail' && (
                                                            <div className="mt-2 flex items-center gap-2">
                                                                {!voicemailState?.heard_at && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={markVoicemailHeard}
                                                                        className="ia-btn3d ia-btn3d--go px-2 py-1 text-xs"
                                                                    >
                                                                        Mark heard
                                                                    </button>
                                                                )}
                                                                {voicemailState?.heard_at && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setExpandedVoicemailKeys((current) => ({ ...current, [voicemailKey]: false }))}
                                                                        className="ia-btn3d px-2 py-1 text-xs"
                                                                    >
                                                                        Collapse voicemail
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </MessageBubble>
                                                </div>
                                            </div>
                                            {aiNote && (
                                                <p data-ai-note={aiNote.kind} className={`px-2 text-center text-[11px] leading-snug ${aiNote.kind === 'failed' ? 'text-amber-500' : 'text-gray-500'}`}>
                                                    {aiNote.note}
                                                </p>
                                            )}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                        {lastMessageLabel && (
                            <p className="text-right text-xs text-[#A9B7D0]" data-last-message="true">
                                Last message {lastMessageLabel}
                            </p>
                        )}
                    </div>
                    <ConversationOverlayActions
                        groupControls={accessMode === 'mine'}
                        state={access?.groupState || 'alone'}
                        isHost={Boolean(access?.isHost)}
                        busy={access?.busy}
                        scrolledUp={threadScroll.scrolledUp}
                        newCount={threadScroll.newCount}
                        onJumpToLatest={threadScroll.jumpToLatest}
                        onInvite={access?.onInvite}
                        onPassTo={access?.onPassTo}
                        onReturnToCompany={access?.onLeave}
                        onLeave={access?.onLeave}
                        onCloseGroup={access?.onCloseGroup}
                        onOpenInvitePanel={() => { handlePanelTabChange('people'); setSidePanelOpen(true); }}
                    />
                    </div>

                    {retryError && <p role="alert" className="px-3 py-1 text-xs text-[#FCA5A5]">{retryError}</p>}
                    {resetOpen && resettableAddress && (
                        <ResetTestContactDialog
                            address={resettableAddress}
                            onDone={(result) => { resetResultRef.current = result; }}
                            onClose={() => {
                                // the conversation is gone after a reset: tell the list only once the person has read the result
                                const result = resetResultRef.current;
                                resetResultRef.current = null;
                                setResetOpen(false);
                                if (result) onTestContactReset?.(result, resettableAddress);
                            }}
                        />
                    )}
                    {editingScheduled && (
                        <ScheduledEditDialog
                            row={editingScheduled}
                            recipientTimeZone={resolveContactTimeZone(contact) || editingScheduled.recipient_time_zone || recipientTimeZoneForPhone(intakeSession?.contact_phone)}
                            busy={scheduledBusy === editingScheduled.id}
                            error={editScheduledError}
                            onSave={saveScheduledEdit}
                            onClose={() => setEditingScheduled(null)}
                        />
                    )}
                    {readOnly && (
                        <p className={`border-t px-4 py-2 text-sm ${dividerCls}`} data-read-only-banner="true">
                            {access?.gated
                                ? `Owned by ${handlerName}. You can read it, but only people in it can reply. Use Request to join to ask.`
                                : `${access?.readOnlyBy ? `${access.readOnlyBy} is handling this conversation.` : 'A colleague is handling this conversation.'} You can read it, but only people in it can reply. Use Join to ask.`}
                        </p>
                    )}
                    <div className={`p-2 border-t ${dividerCls}`}>
                            <StaffReplyComposer key={intakeSession.id}
                                mailboxActions={<EmailMessageActions message={[...items].reverse().find(item => item.emailSyncId && !item.emailDeleted && !item.emailOutsideFolders)} folders={emailMoveFolders} onAction={async (message, action, value) => {
                                    const result = await supabaseClient.functions.invoke('email-sync-action', { body: { sessionId: message.emailSessionId || intakeSession.id, messageId: message.emailSyncId, action, value } });
                                    if (result.error || result.data?.error) throw new Error(result.data?.error || result.error?.message || 'Mailbox action failed');
                                    onReplySent?.({});
                                }} />}
                                intakeSession={intakeSession}
                                initialBody={initialReplyBody}
                                initialSubject={initialReplySubject}
                                lastSubject={lastEmailSubject}
                                phoneCapability={phoneChannel.capability}
                                onSent={handleSent}
                                scheduleSendEnabled
                                onScheduled={row => { acceptScheduled(row); reloadScheduled(); }}
                                engagementState={readOnly ? { canReply: false } : null}
                                theme={theme}
                                currentUserId={currentUserId}
                                platformDoorContext={platformDoorContext}
                                headerBrandEntityId={headerBrandEntityId}
                                companyName={companyName}
                                staffName={staffNames[currentUserId] || 'Staff'}
                                smsOptedOut={Boolean(contact?.sms_opted_out_at)}
                                sendingIdentities={sendingIdentities}
                                templates={templates}
                                templateCompanyName={templateCompanyName}
                                selectedTemplateSid={selectedTemplateSid}
                                templateVariables={templateVariables}
                                onTemplateSelect={(sid, defaults = null) => {
                                    setSelectedTemplateSid(sid || '');
                                    if (!sid) {
                                        setTemplateVariables({});
                                    } else if (defaults) {
                                        setTemplateVariables(defaults);
                                    }
                                }}
                                onTemplatePanelRequestOpen={handleTemplatePanelRequestOpen}
                                contact={contact}
                                allSessions={allSessions}
                                onSelectedIdentityChange={(identity, channel) => {
                                    if (channel !== 'whatsapp' && channel !== activeReplyChannel && sidePanelTab === 'templates') {
                                        setSidePanelOpen(false);
                                        setSidePanelTab('ai_activity');
                                    }
                                    setActiveSendingIdentity(identity);
                                    setActiveReplyChannel(channel);
                                }}
                            />
                    </div>
                    {/* BL-177 Gate 17b: Internal notes back under the reply box (the pre-Gate-15
                        placement) — collapsible, newest first, one line each, own input. */}
                    <div className={`border-t ${dividerCls}`} data-testid="internal-notes-panel">
                        <button
                            type="button"
                            onClick={() => setInternalNotesOpen((current) => !current)}
                            aria-expanded={internalNotesOpen}
                            aria-controls={`internal-notes-body-${sessionId || 'thread'}`}
                            className={`flex w-full items-center gap-2 px-4 py-1.5 text-left text-xs ${dark ? 'bg-gray-900/40 hover:bg-gray-900/70' : 'bg-gray-50 hover:bg-gray-100'}`}
                        >
                            <span aria-hidden="true" className={`inline-block w-3 ${labelCls}`}>{internalNotesOpen ? '▾' : '▸'}</span>
                            <span className="font-medium">Internal notes</span>
                            {internalNotes.length > 0 && <span className={labelCls}>({internalNotes.length})</span>}
                            {unreadInternalNotes > 0 && (
                                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white ${closedQuiet ? 'bg-[#6B7280]' : 'bg-red-600'}`} aria-label={`${unreadInternalNotes} unread internal notes`}>
                                    {unreadInternalNotes}
                                </span>
                            )}
                            <span className={`ml-auto ${labelCls}`}>Team only</span>
                        </button>
                        {internalNotesOpen && (
                            <div id={`internal-notes-body-${sessionId || 'thread'}`} className="space-y-2 px-4 pb-3 pt-2">
                                <div className="flex items-end gap-2">
                                    <textarea
                                        ref={noteInputRef}
                                        rows={1}
                                        value={manualNote}
                                        onChange={(event) => setManualNote(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' && !event.shiftKey) {
                                                event.preventDefault();
                                                addManualInternalNote();
                                            }
                                        }}
                                        placeholder="Add an internal note…"
                                        className={`min-h-8 flex-1 resize-y rounded border px-2 py-1 text-sm ${dark ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white'}`}
                                        aria-label="Add internal note"
                                    />
                                    <button
                                        type="button"
                                        onClick={addManualInternalNote}
                                        disabled={!manualNote.trim()}
                                        className="rounded bg-[var(--btn-fill)] px-3 py-1.5 text-xs font-medium text-[var(--btn-ink)] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Post
                                    </button>
                                </div>
                                <InternalNotesStatusControl intakeSession={intakeSession} onSetFlag={onSetFlag} theme={theme} />
                                {notesNewestFirst.length === 0 ? (
                                    <p className={`text-xs ${labelCls}`}>No internal notes yet.</p>
                                ) : (
                                    <ul className="max-h-40 overflow-auto" aria-label="Internal notes, newest first">
                                        {notesNewestFirst.map((entry) => {
                                            const expanded = Boolean(expandedNoteIds[entry.id]);
                                            const who = entry.type === 'system' && !entry.userId ? 'AI Concierge' : entry.userLabel;
                                            return (
                                                <li key={entry.id} data-note-type={entry.type}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedNoteIds((current) => ({ ...current, [entry.id]: !current[entry.id] }))}
                                                        aria-expanded={expanded}
                                                        title={`${who} · ${formatStamp(entry.createdAt)}\n${entry.text}`}
                                                        className={`block w-full rounded px-1 py-0.5 text-left text-xs ${expanded ? 'whitespace-pre-wrap break-words' : 'truncate'} ${dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-100'}`}
                                                    >
                                                        <span className="font-medium">{who}</span>
                                                        <span className={labelCls}> · {formatNoteStamp(entry.createdAt)} · </span>
                                                        <span className={entry.type === 'system' ? `italic ${labelCls}` : ''}>{entry.text}</span>
                                                    </button>
                                                    {entry.linkUrl && (
                                                        <a href={entry.linkUrl} className="ml-1 px-1 text-xs font-semibold underline" data-testid="internal-note-link">{entry.linkLabel || 'Open'}</a>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <CommunicationsThreadSidePanel
                    templatePanelMode={templatePanelMode} templatePanelChannel={templatePanelChannel}
                    client={supabaseClient}
                    canEditTemplates={isCompanyAdmin}
                    open={sidePanelOpen}
                    pinned={sidePanelPinned}
                    onToggleOpen={() => setSidePanelOpen((current) => !current)}
                    onTogglePinned={handleTogglePinnedPanel}
                    activeTab={sidePanelTab === 'people' && accessMode !== 'mine' ? 'ai_activity' : sidePanelTab}
                    onTabChange={handlePanelTabChange}
                    selectedChannel={activeReplyChannel}
                    onWhatsAppFlagChange={onWhatsAppFlagChange}
                    theme={theme}
                    intakeSession={intakeSession}
                    contact={contact}
                    contactName={contactName}
                    address={address}
                    templates={templates}
                    templateCompanyName={templateCompanyName}
                    activeSendingIdentity={activeSendingIdentity}
                    selectedTemplateSid={selectedTemplateSid}
                    templateVariables={templateVariables}
                    onTemplateSelect={(sid, defaults) => {
                        setSelectedTemplateSid(sid || '');
                        setTemplateVariables(defaults || {});
                    }}
                    onTemplateVariableChange={(key, value) => setTemplateVariables((current) => ({ ...current, [key]: value }))}
                    currentUserId={currentUserId}
                    peopleTab={accessMode === 'mine' ? {
                        people: access?.people || [],
                        loading: access?.peopleLoading,
                        error: access?.peopleError,
                        onInvite: access?.onInvite,
                        onLeave: access?.onLeave,
                        busy: access?.busy,
                        pendingInvite: access?.pendingInvite || null,
                        onWithdraw: access?.onWithdraw,
                    } : null}
                />
            </div>
        </div>
    );
}

export default IntakeSessionDetailCard;
